import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { GraduationCap, PlayCircle, CheckCircle2, Award } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { academyMyTraining, academyOpenCertificatePdf } from '../../../services/api';

/** Employee → My Training. Cards grouped by status. */
export default function MyTraining() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { setRows(await academyMyTraining()); } finally { setLoading(false); }
    })();
  }, []);

  const groups = {
    required: rows.filter(r => !r.passed && !r.percent),
    inProgress: rows.filter(r => !r.passed && r.percent > 0 && r.percent < 100),
    completed: rows.filter(r => r.passed),
  };

  return (
    <div className="p-6 space-y-6" data-testid="my-training">
      <div className="flex items-center gap-2 text-slate-500 uppercase text-xs tracking-widest">
        <GraduationCap className="h-3.5 w-3.5" />Lutrom Academy
      </div>
      <h1 className="text-2xl font-display font-bold text-slate-900">My Training</h1>
      {loading && <p className="text-slate-500">Loading…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-slate-500">No training assigned yet.</p>
      )}
      {['required', 'inProgress', 'completed'].map((k) => (
        groups[k].length > 0 && (
          <section key={k} className="space-y-2">
            <h2 className="text-sm uppercase tracking-widest text-slate-500">
              {k === 'required' ? 'Required' : k === 'inProgress' ? 'In progress' : 'Completed'}
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              {groups[k].map((r) => (
                <div key={r.assignmentId}
                  className="bg-white border rounded-md p-4 flex flex-col gap-2"
                  data-testid={`training-card-${r.courseId}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold text-slate-900">{r.title}</div>
                      <div className="text-xs text-slate-500">{r.category} · v{r.version}</div>
                    </div>
                    {r.passed
                      ? <Badge className="bg-emerald-600">Complete</Badge>
                      : r.percent > 0
                        ? <Badge variant="secondary">{r.percent}%</Badge>
                        : <Badge variant="outline">New</Badge>}
                  </div>
                  {r.percent > 0 && !r.passed && (
                    <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${r.percent}%` }} />
                    </div>
                  )}
                  <div className="flex gap-2 pt-1">
                    {r.passed ? (
                      <>
                        <div className="flex items-center gap-1 text-emerald-700 text-sm">
                          <CheckCircle2 className="h-4 w-4" /> Score {r.lastScore}%
                        </div>
                        {r.certNumber && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                await academyOpenCertificatePdf(r.certNumber);
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || 'Could not open certificate');
                              }
                            }}
                            data-testid={`training-cert-${r.courseId}`}
                          >
                            <Award className="h-3 w-3 mr-1" /> Certificate
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button asChild size="sm" data-testid={`training-launch-${r.courseId}`}>
                        <Link to={`/employee/training/${r.courseId}/${r.version}`}>
                          <PlayCircle className="h-3 w-3 mr-1" />
                          {r.percent > 0 ? 'Continue' : 'Start course'}
                        </Link>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )
      ))}
    </div>
  );
}
